
import React, { useMemo } from 'react';
import { TrackDownload } from '@/lib/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltipContent, ChartTooltip } from '@/components/ui/chart';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, ComposedChart } from 'recharts';
import { useIsMobile } from '@/hooks/use-mobile';
import { addDays, subDays, format, parseISO, startOfDay, isWithinInterval, eachDayOfInterval } from 'date-fns';

interface DownloadsChartProps {
  uploads: TrackDownload[];
  timeRange: string;
  useMockData?: boolean;
}

// Get the date range based on the selected time range
const getDateRange = (timeRange: string) => {
  const today = new Date();
  
  if (timeRange === '7d') {
    return { start: subDays(today, 7), end: today };
  } else if (timeRange === '30d') {
    return { start: subDays(today, 30), end: today };
  } else if (timeRange === '90d') {
    return { start: subDays(today, 90), end: today };
  }
  
  // For "all" timeRange, use earliest track upload date or default to 90 days
  return { start: subDays(today, 90), end: today };
};

const DownloadsChart: React.FC<DownloadsChartProps> = ({ uploads, timeRange, useMockData = false }) => {
  const isMobile = useIsMobile();
  
  const chartData = useMemo(() => {
    console.log('Chart data processing, uploads:', uploads.length, 'useMockData:', useMockData);
    
    const dateRange = getDateRange(timeRange);
    console.log('Date range:', dateRange);
    
    // Create array of all dates in range
    const dateArray = eachDayOfInterval({ 
      start: dateRange.start, 
      end: dateRange.end 
    });
    
    // Initialize with zero downloads for each date
    const dailyDownloads = dateArray.map(date => ({
      date,
      formattedDate: format(date, 'MMM dd'),
      downloads: 0
    }));
    
    // Include all tracks for "all" timeRange, otherwise filter by date range
    let tracksInRange = uploads;
    if (timeRange !== 'all') {
      tracksInRange = uploads.filter(track => {
        const trackDate = new Date(track.createdAt);
        return isWithinInterval(trackDate, {
          start: startOfDay(dateRange.start),
          end: dateRange.end
        });
      });
    }
    
    console.log('Tracks in range:', tracksInRange.length);
    
    // For each track, add its download count to the appropriate day
    tracksInRange.forEach(track => {
      if (track.downloadCount > 0) {
        const trackDate = new Date(track.createdAt);
        const dateIndex = dailyDownloads.findIndex(
          day => format(day.date, 'yyyy-MM-dd') === format(trackDate, 'yyyy-MM-dd')
        );
        
        if (dateIndex >= 0) {
          dailyDownloads[dateIndex].downloads += track.downloadCount;
        }
      }
    });
    
    // Add cumulative data for the line chart
    let cumulativeDownloads = 0;
    const result = dailyDownloads.map(day => {
      cumulativeDownloads += day.downloads;
      return {
        ...day,
        cumulative: cumulativeDownloads
      };
    });
    
    console.log('Final chart data:', result);
    return result;
  }, [uploads, timeRange]);

  return (
    <Card className="w-full overflow-hidden border-border shadow-md">
      <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <CardTitle>Downloads Over Time</CardTitle>
        {useMockData && (
          <div className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-1 rounded self-start sm:self-auto">
            Simulated Data
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <div className="h-[300px] w-full p-4">
          <ChartContainer
            className="h-[300px] w-full animate-fade-in"
            config={{
              downloads: {
                label: "Downloads",
                color: "#8884d8"
              },
              cumulative: {
                label: "Cumulative Downloads",
                color: "#82ca9d"
              }
            }}
          >
            <ComposedChart 
              data={chartData}
              margin={{ top: 10, right: 20, left: 10, bottom: 60 }}
            >
              <defs>
                <linearGradient id="colorDownloads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#444" />
              <XAxis 
                dataKey="formattedDate" 
                angle={-45} 
                textAnchor="end" 
                height={60} 
                tick={{ fontSize: isMobile ? 10 : 12 }}
                tickMargin={10}
                stroke="#888"
              />
              <YAxis 
                allowDecimals={false} 
                tick={{ fontSize: isMobile ? 10 : 12 }}
                stroke="#888"
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
              />
              <Area 
                type="monotone"
                dataKey="downloads"
                fill="url(#colorDownloads)"
                fillOpacity={0.3}
                stroke="#8884d8"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="cumulative" 
                stroke="#82ca9d" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
              />
              <Legend 
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ bottom: -20 }}
              />
            </ComposedChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default DownloadsChart;
